import { DownloadIcon, ExternalLinkIcon } from '@chakra-ui/icons';
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  Link as ChakraLink,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Tab,
  Table,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FaBook, FaRegFolderOpen } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { gapi } from 'gapi-script';
import { useSelector } from '../store/store';
import { Messages } from '../contexts/constants';
import { useManualAPI, ManualType, ManualAPIResponse } from './api/ManualApi';
import { useSpreadsheetTemplateAPI, SpreadsheetTemplateType, SpreadsheetTemplateAPIResponse } from './api/SpreadsheetTemplateApi';
import { useHowToVideoAPI, HowToVideoType, HowToVideoAPIResponse } from './api/HowToVideoApi';
import ManualAddModal from './ManualAddModal';
import ConfirmModal from './ConfirmModal';
import HelpIcon from '../pages/InvestmentScreening/HelpIcon';

type ManualModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

/** 1ページあたりの表示件数 */
const ITEMS_PER_PAGE = 10;

/**
 * Google Sheets の URL からスプレッドシートIDを抽出する
 */
const extractSpreadsheetId = (url: string): string | null => {
  const match = url.match(/\/spreadsheets\/d\/([a-zA-Z0-9_-]+)/);
  return match ? match[1] : null;
};

/**
 * マニュアル・テンプレート一覧モーダル
 */
const ManualModal: React.FC<ManualModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const isHeadquartersManagement = useSelector((state) => state.employee.isHeadquartersManagement);

  // マニュアル用 state
  const [files, setFiles] = useState<ManualType[]>([]);
  const [selectedManual, setSelectedManual] = useState<ManualType | null>(null);
  const [isUpdate, setIsUpdate] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);

  // テンプレート用 state
  const [templates, setTemplates] = useState<SpreadsheetTemplateType[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<SpreadsheetTemplateType | null>(null);
  const [isTemplateUpdate, setIsTemplateUpdate] = useState<boolean>(false);
  const [templateCurrentPage, setTemplateCurrentPage] = useState<number>(1);

  // 操作説明動画用 state
  const [howToVideoItems, setHowToVideoItems] = useState<HowToVideoType[]>([]);
  const [directoryPath, setDirectoryPath] = useState<HowToVideoType[]>([]); // パンくず用スタック（ルートは空配列）
  const [selectedVideo, setSelectedVideo] = useState<HowToVideoType | null>(null);
  const [isVideoUpdate, setIsVideoUpdate] = useState<boolean>(false);

  // 操作説明動画 並べ替え用 state
  const [isVideoReorderMode, setIsVideoReorderMode] = useState<boolean>(false);
  const [draggedVideoIndex, setDraggedVideoIndex] = useState<number | null>(null);
  const [isReordering, setIsReordering] = useState<boolean>(false);

  // 現在地（末尾要素の howToVideoId。ルートなら null）
  const currentParentId = directoryPath.length > 0 ? directoryPath[directoryPath.length - 1].howToVideoId : null;

  // 一括取得した howToVideoItems を現在地の parentId でフィルタリング（directory / link が混在しうる）
  const currentItems = useMemo(
    () => howToVideoItems.filter((item) => (item.parentId || null) === currentParentId).sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)),
    [howToVideoItems, currentParentId]
  );

  // マニュアルのページネーション
  const totalPages = Math.ceil(files.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedFiles = files.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  // テンプレートのページネーション
  const templateTotalPages = Math.ceil(templates.length / ITEMS_PER_PAGE);
  const templateStartIndex = (templateCurrentPage - 1) * ITEMS_PER_PAGE;
  const paginatedTemplates = templates.slice(templateStartIndex, templateStartIndex + ITEMS_PER_PAGE);

  // マニュアル追加モーダル
  const {
    isOpen: isManualAddModalOpen,
    onOpen: onManualAddModalOpen,
    onClose: onManualAddModalClose,
  } = useDisclosure();

  // テンプレート追加モーダル
  const {
    isOpen: isTemplateAddModalOpen,
    onOpen: onTemplateAddModalOpen,
    onClose: onTemplateAddModalClose,
  } = useDisclosure();

  // マニュアル削除確認モーダル
  const {
    isOpen: isDeleteModalOpen,
    onOpen: onDeleteModalOpen,
    onClose: onDeleteModalClose,
  } = useDisclosure();

  // テンプレート削除確認モーダル
  const {
    isOpen: isTemplateDeleteModalOpen,
    onOpen: onTemplateDeleteModalOpen,
    onClose: onTemplateDeleteModalClose,
  } = useDisclosure();

  // 操作説明動画 追加/編集モーダル
  const {
    isOpen: isVideoAddModalOpen,
    onOpen: onVideoAddModalOpen,
    onClose: onVideoAddModalClose,
  } = useDisclosure();

  // 操作説明動画 削除確認モーダル
  const {
    isOpen: isVideoDeleteModalOpen,
    onOpen: onVideoDeleteModalOpen,
    onClose: onVideoDeleteModalClose,
  } = useDisclosure();

  const { getManual, putManual, updateManual, deletedManual } = useManualAPI();
  const { getSpreadsheetTemplate, putSpreadsheetTemplate, updateSpreadsheetTemplate, deletedSpreadsheetTemplate } = useSpreadsheetTemplateAPI();
  const { getHowToVideo, putHowToVideo, updateHowToVideo, deletedHowToVideo, reorderHowToVideo } = useHowToVideoAPI();

  /** マニュアル一覧を取得 */
  const fetchManual = useCallback(async () => {
    try {
      const result = await getManual();
      const sortedResult = (result || []).sort((a, b) =>
        a.manualName.localeCompare(b.manualName, 'ja')
      );
      setFiles(sortedResult);
    } catch (error) {
      toast({
        title: t('common.label.error'),
        description: t('components.ManualModal.toast.fetchFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** テンプレート一覧を取得 */
  const fetchTemplate = useCallback(async () => {
    try {
      const result = await getSpreadsheetTemplate();
      const sortedResult = (result || []).sort((a, b) =>
        a.templateName.localeCompare(b.templateName, 'ja')
      );
      setTemplates(sortedResult);
    } catch (error) {
      toast({
        title: t('common.label.error'),
        description: t('components.ManualModal.toast.fetchFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** 操作説明動画一覧を取得 */
  const fetchHowToVideo = useCallback(async () => {
    try {
      const result = await getHowToVideo();
      setHowToVideoItems(result || []);
    } catch (error) {
      toast({
        title: t('common.label.error'),
        description: t('components.ManualModal.toast.fetchFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    fetchManual();
    fetchTemplate();
    fetchHowToVideo();
    setSelectedManual(null);
    setSelectedTemplate(null);
    setDirectoryPath([]);
    setSelectedVideo(null);
    setCurrentPage(1);
    setTemplateCurrentPage(1);
    setIsVideoReorderMode(false);
    setDraggedVideoIndex(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (files.length > 0) {
      const maxPage = Math.ceil(files.length / ITEMS_PER_PAGE);
      if (currentPage > maxPage) setCurrentPage(maxPage);
    }
  }, [files.length, currentPage]);

  useEffect(() => {
    if (templates.length > 0) {
      const maxPage = Math.ceil(templates.length / ITEMS_PER_PAGE);
      if (templateCurrentPage > maxPage) setTemplateCurrentPage(maxPage);
    }
  }, [templates.length, templateCurrentPage]);

  /** API実行とトースト表示の共通処理（マニュアル） */
  const executeManualAction = async (
    apiFunc: () => Promise<ManualAPIResponse>,
    successMsg: string
  ) => {
    try {
      const res = await apiFunc();
      if (res.status === 'expired') {
        toast({
          title: t('components.ManualModal.title'),
          description: Messages.expiredMessage,
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
        return;
      } else if (res.status !== 'success') {
        const apiMessage = res.message;
        toast({
          title: t('components.ManualModal.title'),
          description: apiMessage ? t(apiMessage) : t('common.message.actionFailed'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }
      toast({ title: t('common.label.success'), description: t(res.message), status: 'success', duration: 3000, isClosable: true });
      await fetchManual();
    } catch (error: any) {
      toast({
        title: t('common.label.error'),
        description: t('common.message.actionFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    } finally {
      setSelectedManual(null);
    }
  };

  /** API実行とトースト表示の共通処理（テンプレート） */
  const executeTemplateAction = async (
    apiFunc: () => Promise<SpreadsheetTemplateAPIResponse>,
    successMsg: string
  ) => {
    try {
      const res = await apiFunc();
      if (res.status === 'expired') {
        toast({
          title: t('components.ManualModal.title'),
          description: Messages.expiredMessage,
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
        return;
      } else if (res.status !== 'success') {
        const apiMessage = res.message;
        toast({
          title: t('components.ManualModal.title'),
          description: apiMessage ? t(apiMessage) : t('common.message.actionFailed'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }
      toast({ title: t('common.label.success'), description: t(res.message), status: 'success', duration: 3000, isClosable: true });
      await fetchTemplate();
    } catch (error: any) {
      toast({
        title: t('common.label.error'),
        description: t('common.message.actionFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    } finally {
      setSelectedTemplate(null);
    }
  };

  // マニュアル操作
  const handleSave = async (data: { manualName: string; manualLink: string; isAdminOnly: boolean }) => {
    await executeManualAction(() => putManual(data), t('components.ManualModal.toast.registerSuccess'));
  };
  const handleUpdate = async (data: { manualId: string; manualName: string; manualLink: string; isAdminOnly: boolean }) => {
    await executeManualAction(
      () => updateManual({ ...data, oldManualName: selectedManual?.manualName, updateDate: selectedManual?.updateDate }),
      t('components.ManualModal.toast.updateSuccess')
    );
  };
  const handleDelete = async () => {
    if (!selectedManual) return;
    await executeManualAction(
      () => deletedManual({ manualId: selectedManual.manualId, manualName: selectedManual.manualName, updateDate: selectedManual.updateDate }),
      t('components.ManualModal.toast.deleteSuccess')
    );
    onDeleteModalClose();
  };

  // テンプレート操作
  const handleTemplateSave = async (data: { manualName: string; manualLink: string }) => {
    await executeTemplateAction(
      () => putSpreadsheetTemplate({ templateName: data.manualName, templateLink: data.manualLink }),
      t('components.ManualModal.toast.registerSuccess')
    );
  };
  const handleTemplateUpdate = async (data: { manualId: string; manualName: string; manualLink: string }) => {
    await executeTemplateAction(
      () => updateSpreadsheetTemplate({ templateId: data.manualId, templateName: data.manualName, templateLink: data.manualLink, updateDate: selectedTemplate?.updateDate }),
      t('components.ManualModal.toast.updateSuccess')
    );
  };
  const handleTemplateDelete = async () => {
    if (!selectedTemplate) return;
    await executeTemplateAction(
      () => deletedSpreadsheetTemplate({ templateId: selectedTemplate.templateId, updateDate: selectedTemplate.updateDate }),
      t('components.ManualModal.toast.deleteSuccess')
    );
    onTemplateDeleteModalClose();
  };

  /** API実行とトースト表示の共通処理（操作説明動画） */
  const executeVideoAction = async (
    apiFunc: () => Promise<HowToVideoAPIResponse>,
    successMsg: string
  ) => {
    try {
      const res = await apiFunc();
      if (res.status === 'expired') {
        toast({
          title: t('components.ManualModal.title'),
          description: Messages.expiredMessage,
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
        return;
      } else if (res.status !== 'success') {
        const apiMessage = res.message;
        toast({
          title: t('components.ManualModal.title'),
          description: apiMessage ? t(apiMessage) : t('common.message.actionFailed'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }
      toast({ title: t('common.label.success'), description: t(res.message), status: 'success', duration: 3000, isClosable: true });
      await fetchHowToVideo();
    } catch (error: any) {
      toast({
        title: t('common.label.error'),
        description: t('common.message.actionFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    } finally {
      setSelectedVideo(null);
    }
  };

  // 操作説明動画操作
  const handleVideoSave = async (data: { manualName: string; manualLink: string; isAdminOnly: boolean; type?: 'directory' | 'link' }) => {
    const itemType = data.type ?? 'link';
    await executeVideoAction(
      () =>
        putHowToVideo({
          howToVideoName: data.manualName,
          videoLink: itemType === 'link' ? data.manualLink : undefined,
          parentId: currentParentId,
          type: itemType,
          isAdminOnly: data.isAdminOnly,
        }),
      t('components.ManualModal.toast.registerSuccess')
    );
  };
  const handleVideoUpdate = async (data: { manualId: string; manualName: string; manualLink: string; isAdminOnly: boolean }) => {
    await executeVideoAction(
      () => updateHowToVideo({ howToVideoId: data.manualId, howToVideoName: data.manualName, videoLink: data.manualLink, isAdminOnly: data.isAdminOnly, updateDate: selectedVideo?.updateDate }),
      t('components.ManualModal.toast.updateSuccess')
    );
  };
  const handleVideoDelete = async () => {
    if (!selectedVideo) return;
    await executeVideoAction(
      () => deletedHowToVideo({ howToVideoId: selectedVideo.howToVideoId, updateDate: selectedVideo.updateDate }),
      t('components.ManualModal.toast.deleteSuccess')
    );
    onVideoDeleteModalClose();
  };

  /** 並べ替えモードのON/OFF切り替え */
  const handleToggleVideoReorderMode = () => {
    if (isReordering) return;
    setIsVideoReorderMode((prev) => !prev);
    setDraggedVideoIndex(null);
  };

  const handleVideoDragStart = (e: React.DragEvent<HTMLTableRowElement>, index: number) => {
    if (isReordering) return;
    // tr要素をtableの外でドラッグすると全行がゴースト表示されるため、ドラッグ画像をこの行自体に明示的に固定する
    e.dataTransfer.setDragImage(e.currentTarget, 0, 0);
    setDraggedVideoIndex(index);
  };

  const handleVideoDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleVideoDragEnd = () => {
    setDraggedVideoIndex(null);
  };

  /**
   * ドロップ確定時に並べ替え後の順序を算出し、即時サーバーへ反映する
   */
  const handleVideoDrop = async (targetIndex: number) => {
    if (isReordering || draggedVideoIndex === null || draggedVideoIndex === targetIndex) {
      setDraggedVideoIndex(null);
      return;
    }
    const reordered = [...currentItems];
    const [removed] = reordered.splice(draggedVideoIndex, 1);
    reordered.splice(targetIndex, 0, removed);
    setDraggedVideoIndex(null);

    setIsReordering(true);
    try {
      const res = await reorderHowToVideo({
        parentId: currentParentId,
        orderedIds: reordered.map((item) => item.howToVideoId),
      });
      if (res.status === 'expired') {
        toast({
          title: t('components.ManualModal.title'),
          description: Messages.expiredMessage,
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
        await fetchHowToVideo();
        return;
      } else if (res.status !== 'success') {
        const apiMessage = res.message;
        toast({
          title: t('components.ManualModal.title'),
          description: apiMessage ? t(apiMessage) : t('common.message.actionFailed'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        await fetchHowToVideo();
        return;
      }
      await fetchHowToVideo();
    } catch (error: any) {
      toast({
        title: t('common.label.error'),
        description: t('common.message.actionFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true
      });
      await fetchHowToVideo();
    } finally {
      setIsReordering(false);
    }
  };


  // テンプレートタブでの ManualAddModal 用データ変換
  const selectedTemplateAsManual: ManualType | null = selectedTemplate
    ? { manualId: selectedTemplate.templateId, manualName: selectedTemplate.templateName, manualLink: selectedTemplate.templateLink }
    : null;

  // 操作説明動画タブでの ManualAddModal 用データ変換
  const selectedVideoAsManual: ManualType | null = selectedVideo
    ? { manualId: selectedVideo.howToVideoId, manualName: selectedVideo.howToVideoName, manualLink: selectedVideo.videoLink ?? '', isAdminOnly: selectedVideo.isAdminOnly }
    : null;

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} isCentered size="2xl" closeOnEsc={false}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Box display="flex" alignItems="center" gap={2}>
              <FaBook color="#2B6CB0" />
              <Text fontWeight="bold">{t('components.ManualModal.title')}</Text>
            </Box>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody minH="20vh">
            <Tabs variant="enclosed">
              <TabList>
                <Tab>{t('components.ManualModal.tab.manual')}</Tab>
                <Tab>{t('components.ManualModal.tab.howToVideo')}</Tab>
                <Tab>{t('components.ManualModal.tab.template')}</Tab>
              </TabList>
              <TabPanels>
                {/* マニュアルタブ */}
                <TabPanel px={0}>
                  <Flex mb="2" align="center" justifyContent="flex-end">
                    {isHeadquartersManagement && (
                      <>
                        <Button colorScheme="blue" mr={3} onClick={() => { setIsUpdate(false); onManualAddModalOpen(); }}>
                          {t('common.button.add')}
                        </Button>
                        <Button colorScheme="blue" isDisabled={selectedManual === null} mr={3}
                          onClick={() => { setIsUpdate(true); onManualAddModalOpen(); }}>
                          {t('common.button.edit')}
                        </Button>
                        <Button colorScheme="red" isDisabled={selectedManual === null} onClick={onDeleteModalOpen}>
                          {t('common.button.delete')}
                        </Button>
                      </>
                    )}
                  </Flex>
                  <Box maxH="400px" minH="400px" overflowY="auto">
                    <Table variant="simple" p={5}>
                      <Thead position="sticky" top={0} bg="gray.100" zIndex={1}>
                        <Tr>
                          <Th whiteSpace="nowrap">{t('components.ManualModal.tableHeader.manualName')}</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {paginatedFiles?.map((file) => {
                          const isSelected = selectedManual?.manualId === file.manualId;
                          return (
                            <Tr key={file.manualId} onClick={() => setSelectedManual(file)} cursor="pointer"
                              border={isSelected ? '2px solid #1976D2' : 'none'}
                              backgroundColor={isSelected ? '#E3F2FD' : 'inherit'}
                              _hover={{ backgroundColor: isSelected ? '#E3F2FD' : 'gray.50' }}>
                              <Td maxW="400px">
                                <ChakraLink href={file.manualLink} color="blue.500" isExternal
                                  display="inline-block" whiteSpace="normal" wordBreak="break-all" fontSize="lg">
                                  {file.manualName} <ExternalLinkIcon mb="4px" />
                                </ChakraLink>
                                {file.isAdminOnly && (
                                  <Badge colorScheme="red" ml={2}>
                                    {t('components.ManualModal.label.adminOnly')}
                                  </Badge>
                                )}
                              </Td>
                            </Tr>
                          );
                        })}
                      </Tbody>
                    </Table>
                  </Box>
                  <Flex mt={3} justifyContent="center" alignItems="center" gap={3}
                    visibility={totalPages > 1 ? 'visible' : 'hidden'} h="40px">
                    <Button size="sm" onClick={() => { setCurrentPage((prev) => prev - 1); setSelectedManual(null); }} isDisabled={currentPage <= 1}>
                      {t('common.button.previous')}
                    </Button>
                    <Text fontSize="sm">{currentPage} / {totalPages}</Text>
                    <Button size="sm" onClick={() => { setCurrentPage((prev) => prev + 1); setSelectedManual(null); }} isDisabled={currentPage >= totalPages}>
                      {t('common.button.next')}
                    </Button>
                  </Flex>
                </TabPanel>

                {/* 操作説明動画タブ */}
                <TabPanel px={0}>
                  {/* パンくずリスト */}
                  <Flex mb="2" align="center" wrap="wrap" gap={1}>
                    <ChakraLink
                      color={directoryPath.length === 0 ? 'gray.500' : 'blue.500'}
                      fontWeight={directoryPath.length === 0 ? 'bold' : 'normal'}
                      cursor={directoryPath.length === 0 || isVideoReorderMode ? 'default' : 'pointer'}
                      onClick={() => { if (directoryPath.length > 0 && !isVideoReorderMode) { setDirectoryPath([]); setSelectedVideo(null); } }}
                    >
                      {t('components.ManualModal.button.rootDirectory')}
                    </ChakraLink>
                    {directoryPath.map((dir, index) => {
                      const isLast = index === directoryPath.length - 1;
                      return (
                        <Flex key={dir.howToVideoId} align="center" gap={1}>
                          <Text>/</Text>
                          <ChakraLink
                            color={isLast ? 'gray.500' : 'blue.500'}
                            fontWeight={isLast ? 'bold' : 'normal'}
                            cursor={isLast || isVideoReorderMode ? 'default' : 'pointer'}
                            onClick={() => { if (!isLast && !isVideoReorderMode) { setDirectoryPath(directoryPath.slice(0, index + 1)); setSelectedVideo(null); } }}
                          >
                            {dir.howToVideoName}
                          </ChakraLink>
                        </Flex>
                      );
                    })}
                  </Flex>
                  {/* 並べ替えモード中の案内表示 */}
                  {isVideoReorderMode && (
                    <Box bg="orange.50" p={2} mb={2} borderRadius="md" borderWidth="1px" borderColor="orange.200">
                      <Text color="orange.600" fontSize="sm">
                        {t('components.ManualModal.label.reorderModeText')}
                      </Text>
                    </Box>
                  )}
                  <Flex mb="2" align="center" justifyContent="flex-end">
                    {isHeadquartersManagement && (
                      <>
                        <Button colorScheme="blue" mr={3} isDisabled={isVideoReorderMode}
                          onClick={() => { setIsVideoUpdate(false); onVideoAddModalOpen(); }}>
                          {t('common.button.add')}
                        </Button>
                        <Button colorScheme="blue" isDisabled={selectedVideo === null || isVideoReorderMode} mr={3}
                          onClick={() => { setIsVideoUpdate(true); onVideoAddModalOpen(); }}>
                          {t('common.button.edit')}
                        </Button>
                        <Button colorScheme="red" isDisabled={selectedVideo === null || isVideoReorderMode} mr={3} onClick={onVideoDeleteModalOpen}>
                          {t('common.button.delete')}
                        </Button>
                        <Button colorScheme={isVideoReorderMode ? 'orange' : 'gray'}
                          isDisabled={currentItems.length <= 1 || isReordering}
                          onClick={handleToggleVideoReorderMode}>
                          {isVideoReorderMode
                            ? t('components.ManualModal.button.reorderComplete')
                            : t('components.ManualModal.button.reorder')}
                        </Button>
                      </>
                    )}
                  </Flex>
                  <Box maxH="400px" minH="400px" overflowY="auto">
                    <Table variant="simple" p={5}>
                      <Thead position="sticky" top={0} bg="gray.100" zIndex={1}>
                        <Tr>
                          <Th whiteSpace="nowrap">{t('components.ManualModal.tableHeader.howToVideoName')}</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {currentItems?.map((item, index) => {
                          const isSelected = selectedVideo?.howToVideoId === item.howToVideoId;
                          const dragProps = {
                            draggable: isVideoReorderMode,
                            onDragStart: (e: React.DragEvent<HTMLTableRowElement>) => handleVideoDragStart(e, index),
                            onDragOver: handleVideoDragOver,
                            onDrop: () => handleVideoDrop(index),
                            onDragEnd: handleVideoDragEnd,
                            bg: draggedVideoIndex === index ? 'blue.50' : (isSelected ? '#E3F2FD' : 'inherit'),
                            cursor: isVideoReorderMode ? 'grab' : 'pointer',
                          };
                          if (item.type === 'directory') {
                            return (
                              <Tr key={item.howToVideoId}
                                onClick={() => { if (!isVideoReorderMode) setSelectedVideo(isSelected ? null : item); }}
                                border={isSelected ? '2px solid #1976D2' : 'none'}
                                _hover={{ backgroundColor: isSelected ? '#E3F2FD' : 'gray.50' }}
                                {...dragProps}>
                                <Td maxW="400px" display="flex" alignItems="center">
                                  <FaRegFolderOpen color="#2B6CB0" style={{ marginRight: "3px" }} />
                                  <ChakraLink
                                    color="blue.500"
                                    fontSize="lg"
                                    display="inline-block"
                                    alignItems="center"
                                    onClick={(e) => { e.stopPropagation(); if (!isVideoReorderMode) { setDirectoryPath((prev) => [...prev, item]); setSelectedVideo(null); } }}
                                  >
                                    {item.howToVideoName}
                                  </ChakraLink>
                                  {item.isAdminOnly && (
                                    <Badge colorScheme="red" ml={2}>
                                      {t('components.ManualModal.label.adminOnly')}
                                    </Badge>
                                  )}
                                </Td>
                              </Tr>
                            );
                          }
                          return (
                            <Tr key={item.howToVideoId}
                              onClick={() => { if (!isVideoReorderMode) setSelectedVideo(isSelected ? null : item); }}
                              border={isSelected ? '2px solid #1976D2' : 'none'}
                              _hover={{ backgroundColor: isSelected ? '#E3F2FD' : 'gray.50' }}
                              {...dragProps}>
                              <Td maxW="400px">
                                <ChakraLink href={isVideoReorderMode ? undefined : item.videoLink} color="blue.500" isExternal
                                  display="inline-block" whiteSpace="normal" wordBreak="break-all" fontSize="lg"
                                  onClick={(e) => { if (isVideoReorderMode) e.preventDefault(); }}>
                                  {item.howToVideoName} <ExternalLinkIcon mb="4px" />
                                </ChakraLink>
                                {item.isAdminOnly && (
                                  <Badge colorScheme="red" ml={2}>
                                    {t('components.ManualModal.label.adminOnly')}
                                  </Badge>
                                )}
                              </Td>
                            </Tr>
                          );
                        })}
                      </Tbody>
                    </Table>
                  </Box>
                </TabPanel>

                {/* テンプレートタブ */}
                <TabPanel px={0}>
                  <Flex mb="2" align="center" justifyContent="space-between">
                    <HelpIcon>
                      <Text>
                        {t('components.ManualModal.help.downloadExcelHelp1')}
                        {t('components.ManualModal.help.downloadExcelHelp2')}
                        {t('components.ManualModal.help.downloadExcelHelp3')}
                        {t('components.ManualModal.help.downloadExcelHelp4')}
                        {t('components.ManualModal.help.downloadExcelHelp5')}
                      </Text>
                    </HelpIcon>
                    {isHeadquartersManagement && (
                      <Flex>
                        <Button colorScheme="blue" mr={3} onClick={() => { setIsTemplateUpdate(false); onTemplateAddModalOpen(); }}>
                          {t('common.button.add')}
                        </Button>
                        <Button colorScheme="blue" isDisabled={selectedTemplate === null} mr={3}
                          onClick={() => { setIsTemplateUpdate(true); onTemplateAddModalOpen(); }}>
                          {t('common.button.edit')}
                        </Button>
                        <Button colorScheme="red" isDisabled={selectedTemplate === null} onClick={onTemplateDeleteModalOpen}>
                          {t('common.button.delete')}
                        </Button>
                      </Flex>
                    )}
                  </Flex>
                  <Box maxH="400px" minH="400px" overflowY="auto">
                    <Table variant="simple" p={5}>
                      <Thead position="sticky" top={0} bg="gray.100" zIndex={1}>
                        <Tr>
                          <Th whiteSpace="nowrap">{t('components.ManualModal.tableHeader.templateName')}</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {paginatedTemplates?.map((template) => {
                          const isSelected = selectedTemplate?.templateId === template.templateId;
                          return (
                            <Tr key={template.templateId} onClick={() => setSelectedTemplate(isSelected ? null : template)} cursor="pointer"
                              border={isSelected ? '2px solid #1976D2' : 'none'}
                              backgroundColor={isSelected ? '#E3F2FD' : 'inherit'}
                              _hover={{ backgroundColor: isSelected ? '#E3F2FD' : 'gray.50' }}>
                              <Td maxW="400px">
                                <ChakraLink href={template.templateLink} color="blue.500" isExternal
                                  display="inline-block" whiteSpace="normal" wordBreak="break-all" fontSize="lg">
                                  {template.templateName} <ExternalLinkIcon mb="4px" />
                                </ChakraLink>
                              </Td>
                            </Tr>
                          );
                        })}
                      </Tbody>
                    </Table>
                  </Box>
                  <Flex mt={3} alignItems="center" h="40px" position="relative">
                    <Flex position="absolute" left="50%" transform="translateX(-50%)"
                      alignItems="center" gap={3}
                      visibility={templateTotalPages > 1 ? 'visible' : 'hidden'}>
                      <Button size="sm" onClick={() => { setTemplateCurrentPage((prev) => prev - 1); setSelectedTemplate(null); }} isDisabled={templateCurrentPage <= 1}>
                        {t('common.button.previous')}
                      </Button>
                      <Text fontSize="sm">{templateCurrentPage} / {templateTotalPages}</Text>
                      <Button size="sm" onClick={() => { setTemplateCurrentPage((prev) => prev + 1); setSelectedTemplate(null); }} isDisabled={templateCurrentPage >= templateTotalPages}>
                        {t('common.button.next')}
                      </Button>
                    </Flex>
                  </Flex>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </ModalBody>
          <ModalFooter />
        </ModalContent>
      </Modal>

      {/* マニュアル追加/編集モーダル */}
      {isManualAddModalOpen && (
        <ManualAddModal
          isOpen={isManualAddModalOpen}
          onClose={onManualAddModalClose}
          onSave={handleSave}
          onUpdate={handleUpdate}
          formData={selectedManual}
          isUpdate={isUpdate}
        />
      )}

      {/* テンプレート追加/編集モーダル（ManualAddModal を流用） */}
      {isTemplateAddModalOpen && (
        <ManualAddModal
          isOpen={isTemplateAddModalOpen}
          onClose={onTemplateAddModalClose}
          onSave={handleTemplateSave}
          onUpdate={handleTemplateUpdate}
          formData={selectedTemplateAsManual}
          isUpdate={isTemplateUpdate}
          titleKey="components.ManualAddModal.templateTitle"
          nameKey="components.ManualAddModal.template"
          showAdminOnlyOption={false}
        />
      )}

      {/* 操作説明動画 追加/編集モーダル（ManualAddModal を流用） */}
      {isVideoAddModalOpen && (
        <ManualAddModal
          isOpen={isVideoAddModalOpen}
          onClose={onVideoAddModalClose}
          onSave={handleVideoSave}
          onUpdate={handleVideoUpdate}
          formData={selectedVideoAsManual}
          isUpdate={isVideoUpdate}
          titleKey="components.ManualAddModal.howToVideoTitle"
          nameKey="components.ManualAddModal.howToVideo"
          showAdminOnlyOption={true}
          showTypeSelector={true}
          currentType={selectedVideo?.type}
        />
      )}

      {/* マニュアル削除確認モーダル */}
      <ConfirmModal
        isOpen={isDeleteModalOpen}
        title={t('components.ManualModal.deleteConfirm.title')}
        message={t('components.ManualModal.deleteConfirm.message', { name: selectedManual?.manualName ?? '' })}
        onOk={handleDelete}
        onCancel={onDeleteModalClose}
        okLabel={t('common.button.delete')}
        cancelLabel={t('common.button.cancel')}
      />

      {/* テンプレート削除確認モーダル */}
      <ConfirmModal
        isOpen={isTemplateDeleteModalOpen}
        title={t('components.ManualModal.deleteConfirm.title')}
        message={t('components.ManualModal.deleteConfirm.message', { name: selectedTemplate?.templateName ?? '' })}
        onOk={handleTemplateDelete}
        onCancel={onTemplateDeleteModalClose}
        okLabel={t('common.button.delete')}
        cancelLabel={t('common.button.cancel')}
      />

      {/* 操作説明動画 削除確認モーダル */}
      <ConfirmModal
        isOpen={isVideoDeleteModalOpen}
        title={t('components.ManualModal.deleteConfirm.title')}
        message={
          selectedVideo?.type === 'directory'
            ? t('components.ManualModal.confirm.deleteDirectoryMessage', { name: selectedVideo?.howToVideoName ?? '' })
            : t('components.ManualModal.deleteConfirm.message', { name: selectedVideo?.howToVideoName ?? '' })
        }
        onOk={handleVideoDelete}
        onCancel={onVideoDeleteModalClose}
        okLabel={t('common.button.delete')}
        cancelLabel={t('common.button.cancel')}
      />
    </>
  );
};

export default ManualModal;
